import { IScope } from 'angular';
import { PartialObserver, pipe, tap } from 'rxjs';
import { takeUntilDestroyed } from './take-until-destroyed';

function noop() {}

/**
 * @example
 *
 * ```ts
 *  const ctrl = this;
 *  ctrl.vm = {
 *    showCancelMembership: false,
 *  };
 *
 *  ordersDetailService.vm$.pipe(
 *    safeApply($scope, {
 *      next: vm => {
 *        ctrl.vm = vm;
 *      },
 *    })
 *  ).subscribe();
 * ```
 */
export function safeApply<T>($scope: IScope, observer: PartialObserver<T>) {
  const onNext = observer?.next ?? noop;
  const onError = observer?.error ?? noop;
  const onComplete = observer?.complete ?? noop;

  // When a digest is already running, invoke the observer synchronously so it is
  // picked up by the current cycle. Otherwise schedule it with `$evalAsync`
  // instead of starting a synchronous `$apply`/`$digest`.
  //
  // Why `$evalAsync` and not `$apply`: these observers are fed by Angular 18
  // services (RxJS `BehaviorSubject`s). An emission can arrive synchronously as
  // a side effect of an AngularJS `$broadcast` (e.g. `Payment::Reset` ->
  // paymentService.send -> stateSubject.next). A synchronous `$apply` here would
  // run a nested `$digest` *during* that broadcast, letting an `ng-if` destroy
  // the very scope the broadcast is still traversing -> AngularJS then reads
  // `$$nextSibling` of a null/severed scope and throws. `$evalAsync` defers the
  // work to the digest's async queue (or schedules a fresh digest when none is
  // running), so the scope tree stays intact for the duration of the broadcast.
  return pipe(
    tap<T>({
      next: (data) => {
        if ($scope.$$phase || $scope.$root.$$phase) {
          onNext(data);
        } else {
          $scope.$evalAsync(() => onNext(data));
        }
      },
      error: (error) => {
        if ($scope.$$phase || $scope.$root.$$phase) {
          onError(error);
        } else {
          $scope.$evalAsync(() => onError(error));
        }
      },
      complete: () => {
        if ($scope.$$phase || $scope.$root.$$phase) {
          onComplete();
        } else {
          $scope.$evalAsync(() => onComplete());
        }
      },
    }),
    takeUntilDestroyed($scope)
  );
}
